import React, { useState, useRef } from 'react';
import { CameraIcon, XMarkIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { contactsAPI } from '../services/api';
import toast from 'react-hot-toast';

interface ScannedContact {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  company: string;
  position: string;
  address1: string;
  city: string;
  addressState: string;
  zip: string;
}

interface BusinessCardScannerProps {
  onScanComplete: (contact: Partial<ScannedContact>) => void;
  onCancel: () => void;
}

const BusinessCardScanner: React.FC<BusinessCardScannerProps> = ({ onScanComplete, onCancel }) => {
  const [scanning, setScanning] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Show preview
    const reader = new FileReader();
    reader.onload = (ev) => setPreview(ev.target?.result as string);
    reader.readAsDataURL(file);

    await scanImage(file);
  };

  const scanImage = async (file: File) => {
    setScanning(true);
    try {
      const response = await contactsAPI.scanBusinessCard(file);
      const contact = response.data.contact;
      toast.success('Business card scanned successfully!');
      onScanComplete(contact);
    } catch (error: any) {
      const msg = error.response?.data?.message || 'Failed to scan business card';
      toast.error(msg);
      setPreview(null);
    } finally {
      setScanning(false);
    }
  };

  const handleRetake = () => {
    setPreview(null);
    fileInputRef.current?.click();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center sm:p-4">
      <div className="modal-mobile bg-white shadow-xl overflow-y-auto max-w-lg w-full rounded-lg">
        <div className="flex items-center justify-between p-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Scan Business Card</h2>
          <button onClick={onCancel} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6">
          {!preview && !scanning && (
            <div className="text-center">
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-gray-300 rounded-lg p-12 cursor-pointer hover:border-primary hover:bg-primary/5 transition-colors"
              >
                <CameraIcon className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 font-medium">Tap to take a photo or choose an image</p>
                <p className="text-sm text-gray-400 mt-2">Supports JPG, PNG, HEIC</p>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleFileSelect}
                className="hidden"
              />
            </div>
          )}

          {preview && (
            <div className="space-y-4">
              <div className="relative rounded-lg overflow-hidden bg-gray-100">
                <img src={preview} alt="Business card" className="w-full object-contain max-h-64" />
                {scanning && (
                  <div className="absolute inset-0 bg-black bg-opacity-50 flex flex-col items-center justify-center">
                    <ArrowPathIcon className="w-10 h-10 text-white animate-spin mb-3" />
                    <p className="text-white font-medium">Scanning card...</p>
                    <p className="text-white/70 text-sm mt-1">Extracting contact info</p>
                  </div>
                )}
              </div>
              {!scanning && (
                <div className="flex gap-3">
                  <button
                    onClick={handleRetake}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Retake
                  </button>
                  <button
                    onClick={onCancel}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BusinessCardScanner;
